import { getToolEntitlement } from './toolTier';

const SYMBOL = {
  NF: 'NIFTY',
  NIFTY: 'NIFTY',
  BN: 'BANKNIFTY',
  BANKNIFTY: 'BANKNIFTY',
  FN: 'FINNIFTY',
  FINNIFTY: 'FINNIFTY',
  MN: 'MIDCPNIFTY',
  MIDCPNIFTY: 'MIDCPNIFTY',
  SNX: 'SENSEX',
  SENSEX: 'SENSEX',
};
 
const CHARTS = [
  { test: /strike_spectrum|spectrum/i, family: 'Spectrum' },
  { test: /ntm_oi_change|ntm_oi/i, family: 'NTM OI Change' },
  { test: /oi_change/i, family: 'OI Change' },
  { test: /ntm_volx/i, family: 'NTM VolX' },
  { test: /ntm_vol/i, family: 'NTM Volume' },
  { test: /vol_charts|volume/i, family: 'Volume' },
  { test: /mviz/i, family: 'Futures mViz' },
  { test: /snakes/i, family: 'Snakes' },
  { test: /gamma_density/i, family: 'Gamma Density' },
  { test: /gamma_curves/i, family: 'Gamma Curves' },
  { test: /strike_value/i, family: 'Gamma' },
  { test: /strike_wcash/i, family: 'WCash' },
  { test: /strike_oi/i, family: 'oiViz' },
  { test: /strike_iv/i, family: 'IV Curve' },
  { test: /iv-range|iv_range/i, family: 'IV Range' },
];

const CHART_NAMES = {
  'market-profile': { chart_name: 'Market Profile', chart_type: 'Market Profile' },
  orderflow: { chart_name: 'Orderflow', chart_type: 'Orderflow' },
  'orderflow-preview': { chart_name: 'Orderflow Preview', chart_type: 'Orderflow' },
  'line-chart': { chart_name: 'Line Charts', chart_type: 'Line Charts' },
  'candle-line-chart': { chart_name: 'Smart Candlesticks', chart_type: 'Smart Candlesticks' },
  'bar-chart': { chart_name: 'Bar Charts', chart_type: 'Bar Charts' },
  'stock-chart': { chart_name: 'Stock Chart', chart_type: 'Stock Chart' },
  'payoff-chart': { chart_name: 'Payoff Chart', chart_type: 'Payoff Chart' },
  'greek-chart': { chart_name: 'Greeks Chart', chart_type: 'Greeks Chart' },
  'options-table': { chart_name: 'Options Table', chart_type: 'Options Table' },
  'index-table': { chart_name: 'Index Table', chart_type: 'Index Table' },
  'fii-data': { chart_name: 'FII Data', chart_type: 'FII Data' },
  'position-table': { chart_name: 'Position Table', chart_type: 'Position Table' },
  'global-bookmarks': { chart_name: 'Global Bookmarks', chart_type: 'Global Bookmarks' },
};

const PAGE_NAMES = {
  login: { chart_name: 'Login', chart_type: 'Auth' },
  register: { chart_name: 'Register', chart_type: 'Auth' },
  'email-verification': { chart_name: 'Email Verification', chart_type: 'Auth' },
  'activate-user': { chart_name: 'Activate User', chart_type: 'Auth' },
  'forgot-password': { chart_name: 'Forgot Password', chart_type: 'Auth' },
  'forgot-password-verification': {
    chart_name: 'Forgot Password Verification',
    chart_type: 'Auth',
  },
  'reset-password': { chart_name: 'Reset Password', chart_type: 'Auth' },
  google: { chart_name: 'Google Auth', chart_type: 'Auth' },
  unauthorized: { chart_name: 'Unauthorized', chart_type: 'Auth' },

  landing: { chart_name: 'Landing', chart_type: 'Marketing' },
  pricing: { chart_name: 'Pricing', chart_type: 'Marketing' },
  intro: { chart_name: 'Intro', chart_type: 'Marketing' },
  'intro-manual': { chart_name: 'Intro Manual', chart_type: 'Marketing' },
  'info-manual': { chart_name: 'Info Manual', chart_type: 'Marketing' },
  glossary: { chart_name: 'Glossary', chart_type: 'Marketing' },
  faqs: { chart_name: 'FAQs', chart_type: 'Marketing' },
  faq: { chart_name: 'FAQs', chart_type: 'Marketing' },
  'privacy-policy': { chart_name: 'Privacy Policy', chart_type: 'Marketing' },
  'about-us': { chart_name: 'About Us', chart_type: 'Marketing' },
  'auth-landing': { chart_name: 'Auth Landing', chart_type: 'Marketing' },
  'auth-privacy-policy': { chart_name: 'Privacy Policy', chart_type: 'Marketing' },
  'auth-glossary': { chart_name: 'Glossary', chart_type: 'Marketing' }
};

const SLUG_PREFIXES = new Set(['line-chart', 'candle-line-chart', 'bar-chart', 'hv-stock-chart']);

function normalizePath(pathname) {
  if (!pathname) {
    return '/';
  }
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
}

function normalizeSymbol(value) {
  if (!value) {
    return undefined;
  }
  return SYMBOL[String(value).toUpperCase()] || String(value).toUpperCase();
}

function extractSymbolFromSlug(slug) {
  const firstToken = String(slug).split(/[_-]/)[0];
  return normalizeSymbol(firstToken);
}

function extractFamilyFromSlug(slug) {
  for (const rule of CHARTS) {
    if (rule.test.test(slug)) {
      return rule.family;
    }
  }
  return undefined;
}

function namedChartIdentity(slug) {
  const symbol = extractSymbolFromSlug(slug);
  const chartType = extractFamilyFromSlug(slug) || String(slug).replace(/_/g, ' ');
  const chartName = symbol ? `${symbol} ${chartType}` : chartType;

  return {
    chart_type: chartType,
    chart_name: chartName,
    symbol,
  };
}

let lastInstrument;
let currentUserRole;
let currentUserTier;
let currentPlanTier;
let currentSubscriptionStatus;

export function setAnalyticsUserProperties({
  role,
  user_tier,
  plan_tier,
  subscription_status,
} = {}) {
  currentUserRole = role;
  currentUserTier = user_tier;
  currentPlanTier = plan_tier;
  currentSubscriptionStatus = subscription_status;
}

export function clearAnalyticsUserProperties() {
  currentUserRole = undefined;
  currentUserTier = undefined;
  currentPlanTier = undefined;
  currentSubscriptionStatus = undefined;
  lastInstrument = undefined;
}

export function getAnalyticsUserSnapshot() {
  return {
    role: currentUserRole,
    user_tier: currentUserTier,
    plan_tier: currentPlanTier,
    subscription_status: currentSubscriptionStatus,
  };
}

export function getChartIdentity(pathname, search = '') {
  const path = normalizePath(pathname);
  const searchParams = new URLSearchParams(search);
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  const [prefix, slug] = parts;

  if (prefix.endsWith('_iv-range') || prefix.endsWith('-iv-range')) {
    return namedChartIdentity(prefix);
  }

  if (prefix === 'hv-stock-chart' && slug) {
    const symbol = normalizeSymbol(slug);
    return {
      chart_type: 'HV',
      chart_name: symbol ? `${symbol} HV` : 'HV',
      symbol,
    };
  }

  if (SLUG_PREFIXES.has(prefix) && slug) {
    return namedChartIdentity(slug);
  }

  const workspace = CHART_NAMES[prefix];
  if (workspace && !slug) {
    const symbol = normalizeSymbol(searchParams.get('symbol'));
    const isReplay = searchParams.get('mode') === 'replay';
    const chartType = prefix.startsWith('orderflow') && isReplay ? 'Orderflow Replay' : workspace.chart_type;

    return {
      chart_type: chartType,
      chart_name: workspace.chart_name,
      symbol,
    };
  }

  const page = PAGE_NAMES[prefix];
  if (page) {
    return {
      chart_type: page.chart_type,
      chart_name: page.chart_name,
    };
  }

  return null;
}

function applyUserProperties(properties) {
  if (currentUserRole) {
    properties.role = currentUserRole;
  }
  if (currentUserTier) {
    properties.user_tier = currentUserTier;
  }
  if (currentPlanTier) {
    properties.plan_tier = currentPlanTier;
  }
  if (currentSubscriptionStatus) {
    properties.subscription_status = currentSubscriptionStatus;
  }
}

function applyInstrument(properties, instrument, prefix = '') {
  if (!instrument) {
    return;
  }
  properties[`${prefix}instrument`] = instrument;
  properties[`${prefix}symbol`] = instrument;
}

export function withChartProperties(event) {
  if (!event?.properties) {
    return event;
  }

  const properties = event.properties;
  applyUserProperties(properties);

  const currentPath = properties.$pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  let currentSearch = '';

  if (properties.$current_url) {
    try {
      currentSearch = new URL(properties.$current_url, 'https://charts.vtrender.com').search;
    } catch {
      currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    }
  } else if (typeof window !== 'undefined') {
    currentSearch = window.location.search;
  }

  const current = getChartIdentity(currentPath, currentSearch);

  if (event.event !== '$pageview' && event.event !== '$pageleave') {
    if (!properties.chart_name && current) {
      properties.chart_type = current.chart_type;
      properties.chart_name = current.chart_name;
    }
    if (!properties.instrument && !properties.symbol) {
      applyInstrument(properties, current?.symbol || lastInstrument);
    }
    return event;
  }

  const previous = properties.$prev_pageview_pathname
    ? getChartIdentity(properties.$prev_pageview_pathname)
    : null;
  const requiredTier = getToolEntitlement(currentPath);

  if (requiredTier) {
    properties.required_tier = requiredTier;
  }

  if (current) {
    properties.chart_type = current.chart_type;
    properties.chart_name = current.chart_name;
    applyInstrument(properties, current.symbol);
  }

  const previousInstrument = previous?.symbol || lastInstrument;
  if (previous) {
    properties.prev_chart_type = previous.chart_type;
    properties.prev_chart_name = previous.chart_name;
  }
  applyInstrument(properties, previousInstrument, 'prev_');

  if (current?.symbol) {
    lastInstrument = current.symbol;
  } else if (!current) {
    lastInstrument = undefined;
  }

  return event;
}
